import {
  type DividerBlockModel,
  DividerBlockSchema,
  ParagraphBlockModel,
  ParagraphBlockSchema,
} from '@blocksuite/affine-model';
import { focusTextModel } from '@blocksuite/affine-rich-text';
import type { AffineTextAttributes } from '@blocksuite/affine-shared/types';
import { matchModels } from '@blocksuite/affine-shared/utils';
import type { BlockComponent } from '@blocksuite/std';
import {
  InlineMarkdownExtension,
  type InlineMarkdownMatchContext,
} from '@blocksuite/std/inline';

function getDividerMarkdownTarget({
  inlineEditor,
  inlineRange,
}: InlineMarkdownMatchContext<AffineTextAttributes>) {
  if (inlineEditor.yTextString.slice(0, inlineRange.index).includes('\n')) {
    return null;
  }

  if (!inlineEditor.rootElement) return null;
  const blockComponent =
    inlineEditor.rootElement.closest<BlockComponent>('[data-block-id]');
  if (
    !blockComponent ||
    !matchModels(blockComponent.model, [ParagraphBlockModel]) ||
    blockComponent.model.props.type === 'quote'
  ) {
    return null;
  }

  return blockComponent;
}

export const DividerMarkdownExtension =
  InlineMarkdownExtension<AffineTextAttributes>({
    name: 'divider',
    completionHint: {
      type: 'divider',
      isEligible: props => getDividerMarkdownTarget(props) !== null,
    },
    pattern: /^(-{3,}|\*{3,}|_{3,})\s$/,
    action: props => {
      const blockComponent = getDividerMarkdownTarget(props);
      if (!blockComponent) return;

      const { inlineEditor, inlineRange } = props;
      const { model, std, store } = blockComponent;
      const parent = store.getParent(model);
      if (!parent) return;
      const index = parent.children.indexOf(model);

      store.captureSync();
      inlineEditor.deleteText({
        index: 0,
        length: inlineRange.index,
      });
      store.addBlock<DividerBlockModel>(
        DividerBlockSchema.model.flavour,
        {
          children: model.children,
        },
        parent,
        index
      );

      const nextBlock = parent.children.at(index + 1);
      let id = nextBlock?.id;
      if (!id) {
        id = store.addBlock<ParagraphBlockModel>(
          ParagraphBlockSchema.model.flavour,
          {},
          parent
        );
      }
      focusTextModel(std, id);
    },
  });
